/**
 * Modal showing details for a single endpoint relationship link.
 */

import type { ReactElement } from 'react'
import type { EndpointRelationship } from '../../../types/case-container'
import { logger } from '../../../utils/logger'

interface LinkEndpointSummary {
  hostname?: string
  ipAddresses?: string[] | null
}

interface LinkDetailsModalProps {
  relationship: EndpointRelationship
  sourceEndpoint: LinkEndpointSummary | undefined
  targetEndpoint: LinkEndpointSummary | undefined
  onClose: () => void
}

export function LinkDetailsModal({
  relationship,
  sourceEndpoint,
  targetEndpoint,
  onClose,
}: LinkDetailsModalProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="mx-4 w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-800">
        <Header status={relationship.status} onClose={onClose} />
        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 py-4">
          <ConnectionRow
            sourceEndpoint={sourceEndpoint}
            targetEndpoint={targetEndpoint}
            status={relationship.status}
          />
          <StatusField status={relationship.status} />
          {relationship.connectionType && (
            <Field
              label="Relationship Type"
              value={relationship.connectionType.replaceAll('-', ' ')}
              capitalise
            />
          )}
          {relationship.status === 'open-question' && (
            <OpenQuestionBlock text={relationship.investigationNotes ?? ''} />
          )}
          {relationship.status !== 'open-question' && relationship.investigationNotes && (
            <Field
              label="Investigation Notes"
              value={relationship.investigationNotes}
              preserveLines
            />
          )}
          <TimestampsRow createdAt={relationship.createdAt} updatedAt={relationship.updatedAt} />
          <Field label="ID" value={relationship.id} idStyle />
        </div>
        <Footer relationshipId={relationship.id} onClose={onClose} />
      </div>
    </div>
  )
}

function Header({ status, onClose }: { status: string; onClose: () => void }): ReactElement {
  const title = status === 'open-question' ? 'Open Question' : 'Link Details'
  return (
    <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/50">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      <button
        onClick={onClose}
        className="rounded-md p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
}

function ConnectionRow({
  sourceEndpoint,
  targetEndpoint,
  status,
}: {
  sourceEndpoint: LinkEndpointSummary | undefined
  targetEndpoint: LinkEndpointSummary | undefined
  status: string
}): ReactElement {
  const lineColour = statusColour(status, 'bg')
  const arrowColour = statusColour(status, 'text')
  return (
    <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-900/50">
      <EndpointSummary endpoint={sourceEndpoint} />
      <div className="flex items-center gap-1">
        <div className={`h-0.5 w-8 ${lineColour}`} />
        <svg className={`h-4 w-4 ${arrowColour}`} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <EndpointSummary endpoint={targetEndpoint} />
    </div>
  )
}

function EndpointSummary({
  endpoint,
}: {
  endpoint: LinkEndpointSummary | undefined
}): ReactElement {
  return (
    <div className="flex-1 text-center">
      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {endpoint?.hostname ?? 'Unknown'}
      </p>
      <p className="font-mono text-xs text-slate-500 dark:text-slate-400">
        {endpoint?.ipAddresses?.[0] ?? ''}
      </p>
    </div>
  )
}

function StatusField({ status }: { status: string }): ReactElement {
  const dotColour = statusColour(status, 'bg')
  const label = status === 'open-question' ? 'Open Question' : status
  return (
    <div>
      <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Status
      </label>
      <div className="mt-1 flex items-center gap-2">
        <span className={`h-3 w-3 rounded-full ${dotColour}`} />
        <span className="text-sm font-medium text-slate-700 capitalize dark:text-slate-300">
          {label}
        </span>
      </div>
    </div>
  )
}

function OpenQuestionBlock({ text }: { text: string }): ReactElement {
  return (
    <div>
      <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Question
      </label>
      <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
        <p className="text-sm whitespace-pre-wrap text-amber-800 dark:text-amber-200">
          {text || 'No question text provided'}
        </p>
      </div>
    </div>
  )
}

function TimestampsRow({
  createdAt,
  updatedAt,
}: {
  createdAt: string
  updatedAt?: string | null
}): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-2 dark:border-slate-700">
      <div>
        <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
          Created
        </label>
        <p className="text-xs text-slate-600 dark:text-slate-400">
          {new Date(createdAt).toLocaleString()}
        </p>
      </div>
      {updatedAt && (
        <div>
          <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
            Updated
          </label>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {new Date(updatedAt).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  value,
  capitalise,
  preserveLines,
  idStyle,
}: {
  label: string
  value: string
  capitalise?: boolean
  preserveLines?: boolean
  idStyle?: boolean
}): ReactElement {
  const valueClasses = idStyle
    ? 'text-xs font-mono text-slate-400 dark:text-slate-500'
    : `text-sm text-slate-700 dark:text-slate-300${capitalise ? ' capitalize' : ''}${preserveLines ? ' whitespace-pre-wrap mt-1' : ''}`
  return (
    <div>
      <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {label}
      </label>
      <p className={valueClasses}>{value}</p>
    </div>
  )
}

function Footer({
  relationshipId,
  onClose,
}: {
  relationshipId: string
  onClose: () => void
}): ReactElement {
  return (
    <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/50">
      <button
        onClick={onClose}
        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        Close
      </button>
      <button
        onClick={() => {
          logger.debug('LinkDetailsModal', 'Edit link', relationshipId)
        }}
        className="rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700"
      >
        Edit
      </button>
    </div>
  )
}

function statusColour(status: string, kind: 'bg' | 'text'): string {
  if (status === 'confirmed') return kind === 'bg' ? 'bg-emerald-500' : 'text-emerald-500'
  if (status === 'suspected') return kind === 'bg' ? 'bg-red-500' : 'text-red-500'
  return kind === 'bg' ? 'bg-amber-400' : 'text-amber-400'
}
